import { useTranslation } from 'react-i18next'

import { Trans } from '@/components/Trans'
import {
  DAILY_PURCHASE_OPTIONS,
  DURATION,
  type Duration,
  MONTHLY_PURCHASE_OPTIONS,
  type PurchaseOption,
} from '@/consts/order'
import { cn } from '@/utils/style'

export type PurchaseValue = number | 'custom'

interface PurchaseForSectionProps {
  duration: Duration
  purchaseFor: PurchaseValue
  subscriptionTotalDays?: number
  onChange: (payload: { purchaseFor: PurchaseValue; totalDays: number }) => void
  className?: string
}

export function PurchaseForSection({
  duration,
  purchaseFor,
  subscriptionTotalDays,
  onChange,
  className,
}: PurchaseForSectionProps) {
  const { t } = useTranslation()
  const isDaily = duration === DURATION.DAILY
  const options = isDaily ? DAILY_PURCHASE_OPTIONS : MONTHLY_PURCHASE_OPTIONS
  const maxCustomValue = isDaily ? 180 : 6

  const handleOptionClick = (option: PurchaseOption) => {
    if (option.value === 'custom') {
      onChange({ purchaseFor: 'custom', totalDays: isDaily ? 1 : 30 })
      return
    }

    onChange({ purchaseFor: option.value, totalDays: option.value })
  }

  const handleCustomChange = (value: string) => {
    if (value === '') {
      onChange({ purchaseFor: 'custom', totalDays: 0 })
      return
    }

    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return

    const clampedValue = Math.min(Math.max(Math.trunc(numericValue), 0), maxCustomValue)
    onChange({
      purchaseFor: 'custom',
      totalDays: isDaily ? clampedValue : clampedValue * 30,
    })
  }

  const customDisplayValue =
    !subscriptionTotalDays || subscriptionTotalDays <= 0
      ? ''
      : isDaily
        ? subscriptionTotalDays
        : Math.round(subscriptionTotalDays / 30)

  return (
    <div className={cn('mt-8 px-8', className)}>
      <div className="t-sb mb-2 text-secondary">
        <Trans i18nKey="create.purchaseFor">Subscription Duration</Trans>
      </div>

      <div className="flex flex-wrap gap-3">
        {options.map((option) => {
          const isCustom = option.value === 'custom'
          const isSelected = purchaseFor === option.value

          if (isCustom && isSelected) {
            return (
              <div
                key="custom"
                className={cn(
                  'relative h-11 px-4 rounded-xl border text-sm flex items-center gap-3',
                  'border-secondary bg-highlight/10 text-highlight font-semibold',
                )}
              >
                <span className="t-sb">
                  <Trans i18nKey="create.PurchaseForSection.custom">Custom</Trans>
                </span>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    className="w-[80px] h-8 rounded-md border border-neutral-200 bg-panel-white px-2 text-sm text-primary focus:outline-none focus:ring-1 focus:ring-secondary"
                    min={1}
                    max={maxCustomValue}
                    step={1}
                    value={customDisplayValue}
                    aria-label={t(
                      isDaily
                        ? 'create.PurchaseForSection.customDays'
                        : 'create.PurchaseForSection.customMonths',
                    )}
                    onChange={(event) => handleCustomChange(event.target.value)}
                    onBlur={() => {
                      if (!subscriptionTotalDays || subscriptionTotalDays <= 0) {
                        onChange({ purchaseFor: 'custom', totalDays: isDaily ? 1 : 30 })
                      }
                    }}
                  />
                  <span className="t-xs text-secondary">
                    {isDaily ? (
                      <Trans i18nKey="create.PurchaseForSection.days">Days</Trans>
                    ) : (
                      <Trans i18nKey="create.PurchaseForSection.months">Months</Trans>
                    )}
                  </span>
                </div>
              </div>
            )
          }

          return (
            <button
              key={option.value}
              type="button"
              className={cn(
                'relative h-11 px-8 rounded-xl border text-sm flex items-center gap-2',
                isSelected
                  ? 'border-secondary bg-highlight/10 text-primary font-semibold'
                  : 'border-neutral-200 bg-panel-white text-secondary hover:bg-neutral-50',
              )}
              onClick={() => handleOptionClick(option)}
            >
              <span className="t-sb">
                {isCustom ? (
                  <>
                    <Trans i18nKey="create.PurchaseForSection.custom">Custom</Trans>{' '}
                    <span className="t-xs text-tertiary">
                      {isDaily ? (
                        <Trans i18nKey="create.PurchaseForSection.days">Days</Trans>
                      ) : (
                        <Trans i18nKey="create.PurchaseForSection.months">Months</Trans>
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    {isDaily ? option.value : Math.round(option.value / 30)}{' '}
                    <Trans
                      i18nKey={
                        isDaily
                          ? option.value === 1
                            ? 'create.PurchaseForSection.day'
                            : 'create.PurchaseForSection.days'
                          : option.value === 30
                            ? 'create.PurchaseForSection.month'
                            : 'create.PurchaseForSection.months'
                      }
                    >
                      {isDaily
                        ? option.value === 1
                          ? 'Day'
                          : 'Days'
                        : option.value === 30
                          ? 'Month'
                          : 'Months'}
                    </Trans>
                  </>
                )}
              </span>
            </button>
          )
        })}
      </div>

      <div className="t-xs text-tertiary mt-2">
        {isDaily ? (
          <Trans i18nKey="create.customPeriodMustBeBetweenDays">
            Custom subscription duration must be between 1 and 180 days.
          </Trans>
        ) : (
          <Trans i18nKey="create.customPeriodMustBeBetweenMonths">
            Custom subscription duration must be between 1 and 6 months.
          </Trans>
        )}
      </div>
    </div>
  )
}
